<template>
  <div class="upscaler-container">
    <VjCard v-if="!isProcessing && !resultImage" class="upload-card">
      <div class="upload-card__content">
        <VjUpload v-model="selectedFiles" accept="image/*" :upload-text="'Bild hier hineinziehen'" :browse-text="'Durchsuchen'" :subtitle-text="'PNG, JPG, WebP - Max 50MB'" :max-size="52428800" />
        <VjInputSlider :min="2" :max="16" v-model="scale" :label="scaleLabel" />

        <!-- Settings Accordion -->
        <VjAccordion expanded title="⚙️ Einstellungen">
          <div class="accordion-content">
            <!-- Model Selection -->
            <div class="setting-group">
              <label>Upscaler Modell:</label>
              <VjSelect v-model="model" :options="modelOptions" optionLabel="label" optionValue="value" />
            </div>

            <!-- Face Enhancement Options -->
            <div class="setting-group">
              <div class="checkbox-group">
                <VjCheckbox v-model="enhanceGfpgan" label="👤 Face Restoration" />
              </div>
              <div class="checkbox-group">
                <VjCheckbox v-model="enhanceCodeformer" label="🎨 Skin Details" />
              </div>
              <div v-if="enhanceGfpgan && enhanceCodeformer" class="chain-info">✨ Beide aktiviert: GFPGAN → CodeFormer (beste Qualität)</div>
            </div>
          </div>
        </VjAccordion>

        <VjButton @click="uploadAndUpscale" :disabled="selectedFiles.length === 0 || isProcessing || !scale" :label="isProcessing ? 'Verarbeite...' : '🚀 Upscale'" primary class="upscale-button" />
      </div>
    </VjCard>

    <VjCard class="worker-card" v-if="isProcessing">
      <div class="worker-card--content">
        <VjSpinner />
        <p>{{ processingStatus || 'Verarbeite Bilder...' }}</p>
      </div>
    </VjCard>

    <VjCard :header="resultImage.filename" v-if="resultImage">
      <template #headerActions>
        <VjButton label="🔄 Neues Bild hochladen" @click="resultImage = null" />
      </template>
      <div class="result-header">
        <span v-if="resultImage.error" class="error-badge">{{ resultImage.error }}</span>
      </div>
      <div v-if="!resultImage.error" class="result-image">
        <img :src="resultImage.image" :alt="resultImage.filename" />
      </div>

      <VjButton v-if="!resultImage.error" label="📥 Download" primary @click="downloadImage()" />
    </VjCard>

    <div v-if="error" class="error-message">⚠️ Fehler: {{ error }}</div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { upscalerPod } from '@/services/upscale.service';

  type ResultImage = {
    filename: string;
    jobId?: string;
    image?: string;
    originalSize?: number;
    error?: string | null;
  };

  const selectedFiles = ref<File[]>([]);
  const scale = ref(4);
  const model = ref('real-esrgan-4x');
  const enhanceGfpgan = ref(false);
  const enhanceCodeformer = ref(false);
  const resultImage = ref<ResultImage | null>(null);
  const isProcessing = ref(false);
  const error = ref<string | null>(null);
  const processingStatus = ref('');

  const modelOptions = [
    { label: 'Real-ESRGAN 4x', value: 'real-esrgan-4x' },
    { label: 'UltraSharp 4x', value: 'ultrasharp' },
    { label: 'BSRGAN 2x', value: 'bsrgan-2x' },
  ];

  const scaleLabel = computed(() => `Vergrößerungsfaktor: ${scale.value}x`);

  const uploadAndUpscale = async () => {
    if (selectedFiles.value.length !== 1) {
      error.value = 'Bitte genau eine Datei auswählen';
      return;
    }

    if (!scale.value) {
      error.value = 'Bitte einen Vergrößerungsfaktor auswählen';
      return;
    }

    const selectedFile = selectedFiles.value[0];
    isProcessing.value = true;
    error.value = null;
    resultImage.value = null;
    processingStatus.value = '';

    try {
      // Step 1: Upload image and get job ID (returns immediately)
      processingStatus.value = '📤 Datei wird hochgeladen...';
      console.log('[Upscaler] 📤 Uploading file...');
      const uploadResult = await upscalerPod.upscale(selectedFile, scale.value, model.value, enhanceGfpgan.value, enhanceCodeformer.value);
      console.log('[Upscaler] Upload successful, job ID:', uploadResult.jobId);

      // Step 2: Wait for job to complete with polling
      processingStatus.value = '⏳ Verarbeitung läuft...';
      console.log('[Upscaler] ⏳ Waiting for job to complete...');
      await upscalerPod.waitForJobDone(uploadResult.jobId, 300);

      // Step 3: Download the upscaled result
      processingStatus.value = '📥 Ergebnis wird heruntergeladen...';
      console.log('[Upscaler] 📥 Downloading result...');
      const imageResult = await upscalerPod.getResult(uploadResult.jobId);

      // Step 4: Add to results
      resultImage.value = {
        filename: selectedFile.name,
        jobId: uploadResult.jobId,
        image: imageResult.imageUrl,
        originalSize: selectedFile.size,
        error: null,
      };

      console.log('[Upscaler] ✅ Upscaling completed successfully');
      processingStatus.value = '✅ Fertig!';

      selectedFiles.value = [];
    } catch (fileError) {
      const errorMsg = fileError instanceof Error ? fileError.message : 'Unbekannter Fehler';
      console.error(`[Upscaler] Error processing ${selectedFile.name}:`, fileError);

      resultImage.value = {
        filename: selectedFile.name,
        error: errorMsg,
      };

      error.value = errorMsg;
    } finally {
      isProcessing.value = false;
      // Keep processingStatus visible for a moment on success
      if (!error.value) {
        setTimeout(() => {
          processingStatus.value = '';
        }, 2000);
      } else {
        processingStatus.value = '';
      }
    }
  };

  const downloadImage = () => {
    if (resultImage.value?.image) {
      const a = document.createElement('a');
      a.href = resultImage.value.image;
      a.download = `upscaled-${resultImage.value.filename}`;
      a.click();
    }
  };
</script>

<style scoped>
  .upscaler-container {
    flex: 1;
    padding: var(--space-xl);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .upload-card {
    width: 50%;
  }

  .worker-card {
    width: 25%;
    min-width: 8rem;
  }

  .worker-card--content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-m);
  }

  .upload-card__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-l);
  }

  .accordion-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-m);
    padding: var(--space-m);
  }

  .setting-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-s);
  }

  .setting-group label {
    font-weight: 500;
    font-size: 0.9rem;
  }

  .checkbox-group {
    display: flex;
    align-items: center;
    gap: var(--space-s);
  }

  .chain-info {
    background: rgba(100, 200, 255, 0.1);
    border-left: 3px solid #64c8ff;
    padding: var(--space-s) var(--space-m);
    border-radius: 4px;
    font-size: 0.9rem;
    color: #333;
  }

  .upscale-button {
    width: 100%;
    margin-bottom: 20px;
  }

  .result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .error-badge {
    background: #fee;
    color: #c33;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.85rem;
  }

  .result-image {
    margin: 20px 0;
    border-radius: 8px;
    overflow: hidden;
    max-height: 400px;
  }

  .result-image img {
    width: 100%;
    height: auto;
    display: block;
  }

  .error-message {
    background: #fee;
    color: #c33;
    padding: 15px;
    border-radius: 6px;
    margin-top: 20px;
    border-left: 4px solid #c33;
  }
</style>
